import { THREE } from "@scrolly/renderer";

// Chroma-Key-Material fuer "Farbe transparent maskieren".
//
// Port aus Jans 1.0 (materials/VideoMaterial.ts): Dort war die urspruengliche
// Fassung — die hier bis eben noch stand — bereits als Ursache fuer ruckelndes
// maskiertes Video identifiziert und ersetzt worden. Sie kopierte JEDES Bild
// per drawImage auf ein 2D-Canvas und lud dieses Canvas als Textur hoch; diese
// Kopie plus Upload pro Bild war der Flaschenhals. Zusaetzlich hing die
// Aktualisierung an readyState === HAVE_ENOUGH_DATA, was beim Scrubben (Seek im
// pausierten Zustand) oft nicht erfuellt ist — das Bild blieb dann stehen.
//
// Jetzt wie im unmaskierten Video-Pfad: die dekodierten Bilder gehen als
// THREE.VideoTexture direkt auf die GPU, und requestVideoFrameCallback meldet
// exakt dann ein neues Bild, wenn der Browser eines praesentiert (auch nach
// einem abgeschlossenen Seek waehrend der Pause). Der Shader tastet mit rohem
// uv ab, deshalb entfaellt auch die frueher noetige horizontale Spiegelung
// (repeat.x = -1), die nur die Canvas-Kopie ausglich.
class VideoMaterial {
    material: THREE.ShaderMaterial;
    videoPlayer: HTMLVideoElement;
    texture: THREE.VideoTexture;
    chromaKey: THREE.Color;
    transparency = true;
    private rvfcSupported = false;
    private rvfcHandle: number | null = null;
    private disposed = false;

    constructor(
        videoPlayer: HTMLVideoElement,
        videoParams: {
            chromaKey: THREE.Color;
            transparent: boolean;
            tolerance: number;
        }
    ) {
        this.videoPlayer = videoPlayer;

        this.texture = new THREE.VideoTexture(videoPlayer);
        this.texture.minFilter = THREE.LinearFilter;
        this.texture.magFilter = THREE.LinearFilter;
        this.texture.format = THREE.RGBAFormat;

        this.chromaKey = new THREE.Color(videoParams.chromaKey || new THREE.Color(0x000000));
        const tolerance = videoParams.tolerance === undefined ? 10 : videoParams.tolerance;

        const uniforms = {
            videoTexture: { type: "t", value: this.texture },
            transparent: { type: "f", value: videoParams.transparent ? 1.0 : 0.0 },
            color: { type: "c", value: this.chromaKey },
            tolerance: { type: "f", value: tolerance },
            opacity: { type: "f", value: 1 },
        };

        this.material = new THREE.ShaderMaterial({
            uniforms,
            transparent: true,
            vertexShader: `

            varying vec2 vUv;

            void main() {
                vUv = uv;
                gl_PointSize = 8.0;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }

          `,
            fragmentShader: `

            varying vec2 vUv;
            uniform float opacity;
            uniform sampler2D videoTexture;
            uniform vec3 color;
            uniform float tolerance;
            uniform float transparent;

            void main() {

                vec3 video  = texture2D(videoTexture, vUv).rgb;
                float a = 1.0;
                if (transparent == 1.0) {
                  // tolerance should be something like 10.0
                  a = min((length(video - color) - 0.5) * tolerance, 1.0);
                }
                gl_FragColor = vec4(video, a * opacity);

            }

          `,
        });

        this.rvfcSupported =
            typeof (this.videoPlayer as TODOAny).requestVideoFrameCallback === "function";
        if (this.rvfcSupported) {
            this.startFrameCallback();
        }
    }

    // Textur EINMAL zum Hochladen vormerken — three laedt das dekodierte Bild
    // dann direkt (ohne Canvas-Kopie). An readyState gekoppelt, damit die GPU nie
    // ein noch nicht dekodiertes Bild lesen soll (das wuerde beim Seeken haengen).
    private markFrame() {
        if (this.videoPlayer.readyState >= this.videoPlayer.HAVE_CURRENT_DATA) {
            this.texture.needsUpdate = true;
        }
    }

    // Genau dann neu hochladen, wenn der Browser ein neues Bild praesentiert
    // (auch nach abgeschlossenem Seek im pausierten Zustand). Haelt das Scrubben
    // synchron, OHNE bei jedem Render hochzuladen — die ueberfluessigen Uploads
    // mitten im Seek waren es, die den Zeiger nachziehen liessen.
    private startFrameCallback() {
        const cb = () => {
            if (this.disposed) return;
            this.markFrame();
            this.rvfcHandle = (this.videoPlayer as TODOAny).requestVideoFrameCallback(cb);
        };
        this.rvfcHandle = (this.videoPlayer as TODOAny).requestVideoFrameCallback(cb);
    }

    update() {
        // Mit aktivem requestVideoFrameCallback treibt der Callback die Uploads;
        // die Render-Schleife tut nichts. Nur wenn die API fehlt, pro Bild laden.
        if (this.rvfcSupported) return;
        this.markFrame();
    }

    dispose() {
        this.disposed = true;
        if (
            this.rvfcHandle != null &&
            typeof (this.videoPlayer as TODOAny).cancelVideoFrameCallback === "function"
        ) {
            (this.videoPlayer as TODOAny).cancelVideoFrameCallback(this.rvfcHandle);
        }
        this.rvfcHandle = null;
        this.texture.dispose();
    }
}

export default VideoMaterial;
